import { toFixedCurrency } from '@sportson/core-web/components/FormatPrice';
import decodeHTMLEntities from '@sportson/core-web/utils/decodeHTMLEntities';
import { getProductAvailability } from '@sportson/core-web/utils/getProductAvailability';
import { productBreadcrumbs as getProductBreadcrumbs } from '@sportson/core-web/utils/storm';
import { APP_ORGANIZATION } from 'config/constants';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

const ProductStructuredData = ({
    brand,
    categories,
    currency,
    description,
    ean,
    images,
    mpn,
    name,
    price,
    sku,
    url,
    variants,
    productAvailability,
    averageScore,
    totalReviews,
    isUsedProduct = false,
    statusId,
    color,
    material,
    product,
}) => {
    const storeAvailability = useSelector(({ page }) => page.data.product.storeAvailability);
    const appOrganization = APP_ORGANIZATION;

    const productAvailabilityValue = statusId === 1 ? 'https://schema.org/InStock' : 'https://schema.org/OutOfStock';

    const siteConfig = useSelector(({ application }) => application.site);

    const { protocol, applicationId, languages } = siteConfig;
    const origin = languages.find((language) => language.id === applicationId)?.uri?.replace(/^\/+|\/+$/g, '');

    const { t } = useTranslation();

    const schema = {
        '@context': 'http://schema.org',
        '@type': 'Product',
        name,
        image: images,
        description,
        sku,
        productID: ean,
        gtin13: ean,
        mpn,
        brand: {
            '@type': 'Brand',
            name: brand,
        },
        offers: {
            '@type': 'Offer',
            url,
            priceCurrency: currency,
            price: `${price}`,
            color,
            material,
            itemCondition: isUsedProduct ? 'http://schema.org/UsedCondition' : 'http://schema.org/NewCondition',
            availability: productAvailabilityValue,

            seller: {
                '@type': 'Organization',
                name: appOrganization,
            },
        },
    };

    if (productAvailability.nextDelivery) {
        schema.offers.availabilityStarts = productAvailability.nextDelivery;
    }

    if (variants.length) {
        let lowestPriceProduct = variants.reduce((res, obj) => (obj.price < res.price ? obj : res));

        let highestPriceProduct = variants.reduce((res, obj) => (obj.price > res.price ? obj : res));

        lowestPriceProduct = lowestPriceProduct.price * lowestPriceProduct.vatRate;
        highestPriceProduct = highestPriceProduct.price * highestPriceProduct.vatRate;

        if (currency === 'EUR') {
            lowestPriceProduct = toFixedCurrency(lowestPriceProduct, 2, '.');
            highestPriceProduct = toFixedCurrency(highestPriceProduct, 2, '.');
        } else {
            lowestPriceProduct =
                lowestPriceProduct % 1 !== 0 ? toFixedCurrency(lowestPriceProduct, 2, '.') : lowestPriceProduct;
            highestPriceProduct =
                highestPriceProduct % 1 !== 0 ? toFixedCurrency(highestPriceProduct, 2, '.') : highestPriceProduct;
        }

        schema.offers = {
            '@type': 'AggregateOffer',
            url,
            lowPrice: lowestPriceProduct,
            highPrice: highestPriceProduct,
            priceCurrency: currency,
            offerCount: variants.length,
        };

        const productVariations = variants.map((variation) => {
            const variationAvailability = getProductAvailability(variation, null, 'onHandStore');

            const variationName = variation.variant_name || name;
            let variationImage =
                (variation.format || {}).image || (variation.files[0] && variation.files[0].format.image);
            const variationMpn = variation.manufacturer?.partNo || variation.manufacturer?.part_no || null;
            const variationSku = variation.partNo || variation.part_no || null;
            const variationEan = variation.eanCode || variation.ean_code || null;
            let variationPrice = variation.price * variation.vatRate;
            if (currency === 'EUR') {
                variationPrice = toFixedCurrency(variationPrice, 2, '.');
            } else if (variationPrice % 1 !== 0) {
                variationPrice = toFixedCurrency(variationPrice, 2, '.');
            }

            const availableAtOrFrom = storeAvailability
                .filter((item) => item.partNo === variationSku)[0]
                ?.stores?.map((store) => {
                    const address =
                        store.address?.address && store.address?.postalAddress
                            ? `${store.address?.address}, ${store.address?.postalAddress}`
                            : null;

                    return {
                        '@type': 'Store',
                        name: store.name,
                        branchCode: store.GLN,
                        availability: store.availability,
                        address,
                    };
                });

            variationImage = variationImage.endsWith('.jpg') ? variationImage : `${variationImage}.jpg`;

            const variationSchema = {
                '@context': 'http://schema.org',
                '@type': 'Product',
                name: variationName,
                image: variationImage,
                description,
                sku: variationSku,
                productID: variationEan,
                gtin13: variationEan,
                mpn: variationMpn,
                brand: {
                    '@type': 'Brand',
                    name: brand,
                },
                offers: {
                    '@type': 'Offer',
                    url: `${url}?p=${variation.part_no ?? variation.partNo}`,
                    price: variationPrice,
                    priceCurrency: currency,
                    color,
                    material,
                    itemCondition: isUsedProduct ? 'http://schema.org/UsedCondition' : 'http://schema.org/NewCondition',
                    availability: productAvailabilityValue,
                    seller: {
                        '@type': 'Organization',
                        name: appOrganization,
                    },
                    availableAtOrFrom,
                },
                // TODO: Reviews are not avalible at the moment, add in later stage
                // aggregateRating: null,
                // review: null,
            };

            if (variationAvailability.nextDelivery) {
                variationSchema.offers.availabilityStarts = variationAvailability.nextDelivery;
            }

            return variationSchema;
        });

        schema.hasVariant = productVariations;
    }

    if (averageScore && totalReviews) {
        schema.aggregateRating = {
            '@type': 'AggregateRating',
            ratingValue: averageScore.toString() || null,
            reviewCount: totalReviews.toString() || null,
        };
    }

    const breadcrumbs = [
        {
            '@type': 'ListItem',
            position: 1,
            name: t('home'),
            item: protocol + origin,
        },
    ];

    const productBreadcrumbs = getProductBreadcrumbs(categories);

    if (productBreadcrumbs) {
        productBreadcrumbs.forEach((item, index) => {
            breadcrumbs.push({
                '@type': 'ListItem',
                position: index + 2,
                name: decodeHTMLEntities(item.title),
                item: protocol + origin + item.permalink,
            });
        });
    }

    breadcrumbs.push({
        '@type': 'ListItem',
        position: breadcrumbs.length + 1,
        name: decodeHTMLEntities(name),
    });

    const breadcrumbList = {
        '@context': 'http://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbs,
    };

    return (
        <Helmet>
            <script type="application/ld+json">{JSON.stringify(schema, null, 2)}</script>
            {!!breadcrumbs.length && (
                <script type="application/ld+json">{JSON.stringify(breadcrumbList, null, 0)}</script>
            )}
        </Helmet>
    );
};

ProductStructuredData.propTypes = {
    averageScore: PropTypes.number,
    brand: PropTypes.string.isRequired,
    categories: PropTypes.array.isRequired,
    currency: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    ean: PropTypes.string.isRequired,
    images: PropTypes.array.isRequired,
    mpn: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    productAvailability: PropTypes.shape({
        inStock: PropTypes.bool,
        preOrder: PropTypes.bool,
        nextDelivery: PropTypes.string,
    }).isRequired,
    sku: PropTypes.string.isRequired,
    totalReviews: PropTypes.number,
    url: PropTypes.string.isRequired,
    variants: PropTypes.array.isRequired,
};

ProductStructuredData.defaultProps = {
    averageScore: null,
    totalReviews: null,
};

export default ProductStructuredData;
